'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { useSession, signOut } from 'next-auth/react'
import { isStaff } from '@/lib/roles'
import {
  Menu,
  X,
  Search,
  ShoppingBag,
  ShoppingBasket,
  ShoppingCart,
  User,
  ChevronDown,
  LogOut,
  Package,
  Settings,
  Wheat,
  Cake,
  Cookie,
  Coffee,
  Candy,
  Egg,
  Sandwich,
  Croissant,
  Utensils,
  Sun,
  ArrowRight,
  Tag,
  Star,
  Sparkles,
  FileText,
  ChevronRight,
} from 'lucide-react'
import { GiftIcon, CameraIcon, StoreIcon } from '@animateicons/react/lucide'
import AnimatedCakeIcon, { type AnimatedIconHandle, type AnimatedIconProps } from '@/components/icons/AnimatedCakeIcon'
import { useAnimatedIconLoop, ICON_LOOP_STAGGER } from '@/components/icons/useAnimatedIconLoop'
import { OCCASIONS, occasionHref } from '@/lib/occasions'
import { useCartStore, itemEffectivePrice, type CartNotification } from '@/store/cartStore'
import { nlTimeParts } from '@/lib/nltime'

/* ─── Types ────────────────────────────────────────────────────────────── */
interface Category {
  id:   string
  name: string
  slug: string
  icon: string | null
  /** Admin-set thumbnail (or a representative product image) from the backend. */
  image?: string | null
  /** null/absent for a top-level category — the nav only shows those. */
  parentId?: string | null
  description?: string | null
}

/** Login link that carries the current page as callbackUrl, so signing in
 *  returns the user to where they were (not always /account). */
function loginHref(pathname: string): string {
  return pathname && pathname !== '/inloggen' && pathname !== '/registreren'
    ? `/inloggen?callbackUrl=${encodeURIComponent(pathname)}`
    : '/inloggen'
}

interface NavLink {
  label:        string
  href:         string
  hasMenu?:     boolean
  hasSubLinks?: boolean
  /** Populated from active campaigns at runtime. */
  isCampaign?:  boolean
  /** Special-cased Pasen link (keeps the classic egg styling + /pasen URL). */
  isPasen?:     boolean
  accentColor?: string
}

/* ─── Constants ─────────────────────────────────────────────────────────── */
const BASE_NAV_LINKS: NavLink[] = [
  { label: 'Home',                href: '/'                                      },
  { label: 'Ons Verhaal',         href: '/over-ons'                              },
  { label: 'Voor elke gelegenheid', href: '/genieten-vieren', hasSubLinks: true },
  { label: 'Winkel',              href: '/winkel',           hasMenu: true       },
  { label: 'Openingstijden',      href: '/openingstijden'                        },
  { label: 'Contact',             href: '/contact'                               },
]

/** Animated (hover-triggered) icons — the row hover drives them, see GenietSubLink. */
type AnimatedIcon = React.ForwardRefExoticComponent<AnimatedIconProps & React.RefAttributes<AnimatedIconHandle>>

const GENIETEN_SUB_LINKS: { label: string; href: string; Icon: AnimatedIcon }[] = [
  { label: 'Voor elke gelegenheid', href: '/genieten-vieren',             Icon: GiftIcon          },
  { label: 'Fototaart',           href: '/genieten-vieren/fototaart',     Icon: CameraIcon        },
  { label: 'Slagroomtaart met tekst', href: '/genieten-vieren/slagroomtaart', Icon: AnimatedCakeIcon },
]

/* One sub-menu row. Keeps a ref on the icon so hovering anywhere on the row
   (not just the 18px icon) plays the animation, and stops it on leave. The
   same ref lets the icon idle-loop while the row is on screen — `index`
   staggers it against its neighbours. */
function GenietSubLink({
  sub, index = 0, className, labelClassName, onClick,
}: {
  sub:             (typeof GENIETEN_SUB_LINKS)[number]
  index?:          number
  className:       string
  labelClassName:  string
  onClick?:        () => void
}) {
  const rowRef  = useRef<HTMLAnchorElement>(null)
  const iconRef = useRef<AnimatedIconHandle>(null)
  useAnimatedIconLoop(rowRef, iconRef, { delay: index * ICON_LOOP_STAGGER })
  return (
    <Link
      ref={rowRef}
      href={sub.href}
      onClick={onClick}
      className={className}
      onMouseEnter={() => iconRef.current?.startAnimation()}
      onMouseLeave={() => iconRef.current?.stopAnimation()}
    >
      <sub.Icon ref={iconRef} size={18} className="flex-shrink-0" />
      <span className={labelClassName}>{sub.label}</span>
    </Link>
  )
}

/* ─── Category icon mapping ──────────────────────────────────────────────── */
function getCategoryIconComponent(slug: string) {
  if (slug.includes('granen') || slug.includes('tarwe') || slug.includes('volkoren') || slug.includes('rogge')) return Wheat
  if (slug.includes('brood') || slug.includes('wit') || slug.includes('gevuld')) return Sandwich
  if (slug.includes('bollet') || slug.includes('croissant') || slug.includes('broodje')) return Croissant
  if (slug.includes('beschuit') || slug.includes('cracker')) return Cookie
  if (slug.includes('taart')) return Cake
  if (slug.includes('vlaai') || slug.includes('slofje') || slug.includes('gebak')) return Utensils
  if (slug.includes('koffie')) return Coffee
  if (slug.includes('koek') || slug.includes('cake')) return Cookie
  if (slug.includes('chocolade')) return Candy
  if (slug.includes('pasen')) return Egg
  return ShoppingBag
}

function getCategoryIcon(slug: string) {
  const Icon = getCategoryIconComponent(slug)
  return <Icon size={18} />
}

/* Round category thumbnail — uses the real backend image when available,
   falling back to the slug-derived Lucide icon. Shared by the desktop mega
   menu and the mobile Winkel submenu. */
/* Korte ondertitel per hoofdcategorie in het menu. Komt er een categorie bij
   waarvoor hier niets staat, dan valt hij terug op de eigen omschrijving. */
const CATEGORY_TAGLINES: Record<string, string> = {
  'brood':                  'Vers uit onze oven',
  'broodjes-en-bolletjes':  'Voor elk moment',
  'gebakjes':               'Heerlijk zoet genieten',
  'koffiebroodjes-en-koek': 'Perfect bij de koffie',
  'taart-en-vlaai':         'Voor elke gelegenheid',
  'cadeau':                 'Geef een smakelijk cadeau',
  'geboorte':               'Taart en wegge met naam',
}

function categoryTagline(cat: Category): string {
  const own = CATEGORY_TAGLINES[cat.slug]
  if (own) return own
  const desc = (cat.description ?? '').trim()
  if (desc && desc.length <= 42) return desc
  return 'Bekijk het assortiment'
}

/** Categoriekaart met foto, naam en ondertitel. Gedeeld door het menu op de
 *  desktop en het uitklapmenu op een telefoon. */
function CategoryCard({ cat, onClick }: { cat: Category; onClick?: () => void }) {
  return (
    <Link
      href={`/winkel?category=${cat.slug}`}
      onClick={onClick}
      className="group flex items-center gap-3 p-2.5 rounded-2xl bg-[#FBF1E2] border border-[#D2691E]/12 hover:border-[#D2691E]/35 hover:bg-[#F7E7CE] transition-colors"
    >
      <span className="shrink-0 w-14 h-14 rounded-xl overflow-hidden bg-[#D2691E]/10 text-[#8B4513] flex items-center justify-center">
        {cat.image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={cat.image}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          getCategoryIcon(cat.slug)
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-playfair font-bold text-[15px] leading-tight text-[#2C1810] group-hover:text-[#8B4513] transition-colors">
          {cat.name}
        </span>
        <span className="block text-xs text-[#8B4513]/70 mt-1 truncate">{categoryTagline(cat)}</span>
      </span>
      <ChevronRight size={16} className="shrink-0 text-[#8B4513]/45 group-hover:text-[#8B4513] transition-colors" />
    </Link>
  )
}

/** Brede balk boven- en onderaan het menu: aanbiedingen en de hele winkel. */
function MenuBanner({
  href, onClick, Icon, title, subtitle, tone,
}: {
  href: string
  onClick?: () => void
  Icon: React.ComponentType<{ size?: number }>
  title: string
  subtitle: string
  tone: 'sale' | 'shop'
}) {
  const bg = tone === 'sale'
    ? 'linear-gradient(135deg, #7F1D1D, #B91C1C)'
    : 'linear-gradient(135deg, #6B3410, #8B4513)'
  const bgHover = tone === 'sale'
    ? 'linear-gradient(135deg, #991B1B, #DC2626)'
    : 'linear-gradient(135deg, #8B4513, #A85A20)'
  const fg = tone === 'sale' ? '#FECACA' : '#FDF6EC'
  return (
    <Link
      href={href}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all"
      style={{ background: bg }}
      onMouseEnter={(e) => (e.currentTarget.style.background = bgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = bg)}
    >
      <span
        className="w-10 h-10 flex items-center justify-center rounded-full shrink-0"
        style={{ background: 'rgba(255,255,255,0.14)', color: fg }}
      >
        <Icon size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-playfair font-bold text-base leading-tight" style={{ color: fg }}>
          {title}
        </span>
        <span className="block text-xs mt-0.5" style={{ color: fg, opacity: 0.75 }}>
          {subtitle}
        </span>
      </span>
      <ChevronRight size={18} style={{ color: fg, opacity: 0.85 }} />
    </Link>
  )
}

function CategoryThumb({ cat }: { cat: Category }) {
  return (
    <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full overflow-hidden bg-[#D2691E]/10 text-[#8B4513] group-hover:bg-[#8B4513] group-hover:text-[#FDF6EC] transition-colors">
      {cat.image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={cat.image}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
        />
      ) : (
        getCategoryIcon(cat.slug)
      )}
    </span>
  )
}

/* ─── Search Overlay ────────────────────────────────────────────────────── */
/* Informational pages the search can surface (next to product hits) so a query
   like "Fototaart" offers both the page (info) and the product (to order). */
interface SearchPage {
  title:       string
  description: string
  href:        string
  image:       string
  keywords:    string[]
}

const STATIC_PAGES: SearchPage[] = [
  { title: 'Fototaart',        description: 'Stel je eigen taart met foto samen',     href: '/genieten-vieren/fototaart', image: '/images/taartopdruk/fototaart-vol.webp',          keywords: ['fototaart', 'foto taart', 'taart met foto', 'eigen foto', 'logo taart', 'foto'] },
  { title: 'Slagroomtaart met tekst', description: 'Verse slagroomtaart met eigen tekst', href: '/genieten-vieren/slagroomtaart', image: '/images/taartopdruk/Slagroomtaart-4k-Final-Full.png', keywords: ['slagroomtaart', 'slagroom', 'taart met tekst', 'teksttaart', 'tekst', 'gefeliciteerd'] },
  { title: 'Voor elke gelegenheid', description: 'Taarten en banket voor elk feest',   href: '/genieten-vieren',           image: '/images/taartopdruk/Slagroomtaartje-12-personen.jpg',     keywords: ['gelegenheid', 'gelegenheden', 'genieten', 'vieren', 'feest', 'vieren'] },
  { title: 'Pasen',            description: 'Ons paasassortiment',                     href: '/pasen',                     image: '/images/pasen/Stol-middel-dip-scaled.jpg',               keywords: ['pasen', 'paas', 'paasbrood', 'paasstol', 'easter'] },
  { title: 'Sinterklaas',      description: 'Het Rijssens hartje en meer',             href: '/sinterklaas',               image: '/images/sinterklaas/groot.webp',                         keywords: ['sinterklaas', 'sint', 'speculaas', 'hartje', 'speculaashart', 'rijssens hartje', 'pakjesavond'] },
  { title: 'Aanbiedingen',     description: 'Huidige acties en kortingen',             href: '/aanbiedingen',              image: '/images/DeBakkerij_Slider_02.png',                       keywords: ['aanbieding', 'aanbiedingen', 'korting', 'actie', 'sale', 'deal', 'dagdeal', 'goedkoop'] },
  { title: 'Openingstijden',   description: 'Wanneer zijn we open?',                   href: '/openingstijden',            image: '/images/winkelpand.jpg',                                 keywords: ['openingstijden', 'openingstijd', 'open', 'geopend', 'tijden', 'zondag', 'feestdag', 'gesloten'] },
  { title: 'Contact',          description: 'Adres, telefoon en route',                href: '/contact',                   image: '/images/header-account.jpg',                             keywords: ['contact', 'adres', 'telefoon', 'bellen', 'route', 'locatie', 'e-mail', 'email', 'waar', 'rijssen'] },
  { title: 'Over ons',         description: 'Het verhaal van Bakkerij Voortman',       href: '/over-ons',                  image: '/images/DeBakkerij_Slider_03.png',                       keywords: ['over ons', 'geschiedenis', 'verhaal', 'bakker', 'bakkerij', 'ambacht', '1946'] },
  { title: 'Alle producten',   description: 'Bekijk de hele winkel',                   href: '/winkel',                    image: '/images/DeBakkerij_Slider_01.png',                       keywords: ['winkel', 'producten', 'assortiment', 'bestellen', 'shop', 'webshop'] },
  { title: 'Winkelwagen',      description: 'Je huidige bestelling',                   href: '/winkelwagen',               image: '/images/DeBakkerij_Slider_01.png',                       keywords: ['winkelwagen', 'mandje', 'bestelling', 'afrekenen', 'checkout', 'kassa'] },
  { title: 'Mijn account',     description: 'Bestellingen, gegevens en favorieten',    href: '/account',                   image: '/images/header-account.jpg',                             keywords: ['account', 'inloggen', 'mijn account', 'bestellingen', 'favorieten', 'profiel', 'wachtwoord'] },
]

/* Each occasion from /genieten-vieren is its own hit, carrying that occasion's
   own photo and deep-linking to the page with it already selected — so a
   search for "Bruiloft" shows the bruiloft picture, not a generic taart. */
const OCCASION_PAGES: SearchPage[] = OCCASIONS.map((o) => ({
  title:       o.label,
  description: `Gelegenheid: ${o.description.split('.')[0]}.`,
  href:        occasionHref(o.id),
  image:       o.image,
  keywords:    o.keywords,
}))

const SEARCHABLE_PAGES: SearchPage[] = [...OCCASION_PAGES, ...STATIC_PAGES]

/** Fold accents and punctuation away so "jubileum", "Jubileüm" and "jubileum,"
 *  all reduce to the same thing before comparing. */
function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/* Ranked, so the closest hit is the first row rather than whichever entry
   happens to sit highest in the array. Higher is better; 0 drops the page. */
function scorePage(page: SearchPage, query: string): number {
  const q      = normalize(query)
  const title  = normalize(page.title)
  const terms  = [title, ...page.keywords.map(normalize)]
  const words  = q.split(' ').filter((w) => w.length >= 2)
  if (!q) return 0

  if (title === q) return 100
  if (terms.includes(q)) return 90
  if (title.startsWith(q)) return 80
  if (terms.some((t) => t.startsWith(q))) return 70
  if (title.includes(q)) return 60
  if (terms.some((t) => t.includes(q))) return 50
  // Every word of a multi-word query found somewhere ("taart bruiloft").
  if (words.length > 1 && words.every((w) => terms.some((t) => t.includes(w)))) return 40
  // Typed more than the term itself ("verjaardagstaart" → "verjaardag").
  if (terms.some((t) => t.length >= 4 && q.includes(t))) return 30
  return 0
}

function matchPages(query: string): SearchPage[] {
  if (normalize(query).length < 2) return []
  return SEARCHABLE_PAGES
    .map((page) => ({ page, score: scorePage(page, query) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6)
    .map((r) => r.page)
}

interface SearchProduct {
  id: string
  name: string
  slug: string
  price: number
  salePrice: number | null
  images: string | string[] | null
}

function getFirstImage(images: string | string[] | null): string | null {
  if (!images) return null
  if (Array.isArray(images)) return images[0] ?? null
  try {
    const parsed = JSON.parse(images)
    if (Array.isArray(parsed)) return parsed[0] ?? null
    if (typeof parsed === 'string') return parsed
  } catch {
    if (typeof images === 'string' && images.startsWith('http')) return images
  }
  return null
}

function SearchOverlay({ onClose }: { onClose: () => void }) {
  const [query, setQuery]     = useState('')
  const [results, setResults] = useState<SearchProduct[]>([])
  const inputRef              = useRef<HTMLInputElement>(null)

  /* Page matches are computed synchronously from a static list. */
  const pageResults = matchPages(query)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!query.trim()) { setResults([]); return }
    const id = setTimeout(async () => {
      try {
        const res = await fetch(`/api/products/search?q=${encodeURIComponent(query.trim())}`)
        if (res.ok) setResults(await res.json())
      } catch { /* ignore */ }
    }, 280)
    return () => clearTimeout(id)
  }, [query])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[10vh] px-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.97 }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        style={{ transformOrigin: 'top center' }}
        className="relative w-full max-w-2xl bg-[#FDF6EC] rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#D2691E]/20">
          <Search size={20} className="text-[#8B4513] shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek naar brood, taarten, koeken…"
            className="flex-1 bg-transparent text-[#2C1810] placeholder-[#8B4513]/50 text-lg font-nunito"
            style={{ outline: 'none', boxShadow: 'none', border: 'none' }}
          />
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-[#8B4513] hover:bg-[#8B4513]/10 transition-colors"
            aria-label="Sluiten"
          >
            <X size={20} />
          </button>
        </div>

        {/* Results — pages and products are listed separately */}
        {(pageResults.length > 0 || results.length > 0) && (
          <div className="max-h-[60vh] overflow-y-auto">
            {/* Pagina's */}
            {pageResults.length > 0 && (
              <div>
                <p className="px-5 pt-4 pb-1.5 text-[11px] uppercase tracking-widest font-bold text-[#8B4513]/70">
                  Pagina&rsquo;s
                </p>
                <ul className="divide-y divide-[#D2691E]/10">
                  {pageResults.map((page) => (
                    <li key={page.href}>
                      <Link
                        href={page.href}
                        onClick={onClose}
                        className="flex items-center gap-3 px-5 py-3 hover:bg-[#8B4513]/5 transition-colors"
                      >
                        <span className="relative shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-[#8B4513]/8 flex items-center justify-center text-[#8B4513]">
                          {/* Icon sits behind as a fallback; the image covers it. */}
                          <FileText size={18} className="absolute" />
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={page.image}
                            alt=""
                            className="absolute inset-0"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onError={(e) => { e.currentTarget.style.display = 'none' }}
                          />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-medium text-[#2C1810]">{page.title}</span>
                          <span className="block text-xs text-[#8B4513]/70 truncate">{page.description}</span>
                        </span>
                        <ChevronRight size={16} className="shrink-0 text-[#8B4513]/50" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Producten */}
            {results.length > 0 && (
              <div>
                <p className="px-5 pt-4 pb-1.5 text-[11px] uppercase tracking-widest font-bold text-[#8B4513]/70">
                  Producten
                </p>
                <ul className="divide-y divide-[#D2691E]/10">
                  {results.map((product) => {
                    const imgUrl = getFirstImage(product.images)
                    return (
                      <li key={product.id}>
                        <Link
                          href={`/winkel/product/${product.slug}`}
                          onClick={onClose}
                          className="flex items-center gap-3 px-5 py-3 hover:bg-[#8B4513]/5 transition-colors"
                        >
                          {/* Product thumbnail */}
                          <span className="shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-[#D2691E]/10 flex items-center justify-center text-lg">
                            {imgUrl ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={imgUrl}
                                alt={product.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                            ) : (
                              <Wheat size={18} className="text-[#D2691E]/60" />
                            )}
                          </span>
                          <span className="flex-1 font-medium text-[#2C1810]">{product.name}</span>
                          <span className="text-sm font-semibold text-[#8B4513] shrink-0">
                            {product.salePrice != null
                              ? `€ ${product.salePrice.toFixed(2).replace('.', ',')}`
                              : `€ ${product.price.toFixed(2).replace('.', ',')}`}
                          </span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {query.trim().length >= 2 && pageResults.length === 0 && results.length === 0 && (
          <p className="px-5 py-6 text-[#8B4513] text-center text-sm">
            Geen resultaten gevonden voor &ldquo;{query}&rdquo;
          </p>
        )}

        {!query && (
          <div className="px-5 py-4">
            <p className="text-xs uppercase tracking-widest text-[#8B4513] mb-3 font-semibold">
              Populaire zoekopdrachten
            </p>
            <div className="flex flex-wrap gap-2">
              {['Wit brood', 'Appelflap', 'Slagroomtaart', 'Krentenbollen', 'Speculaas'].map((term) => (
                <button
                  key={term}
                  onClick={() => setQuery(term)}
                  className="px-3 py-1 rounded-full text-sm bg-[#8B4513]/10 text-[#8B4513] hover:bg-[#8B4513]/20 transition-colors font-medium"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}

/* ─── Nav Link with hover underline ─────────────────────────────────────── */
function NavLink({
  href,
  isActive,
  color,
  textShadow,
  children,
}: {
  href: string
  isActive: boolean
  color: string
  textShadow?: string
  children: React.ReactNode
}) {
  const [hovered, setHovered] = useState(false)
  return (
    <Link
      href={href}
      className="relative block px-3 xl:px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap"
      style={{ color, textShadow }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      <AnimatePresence>
        {(isActive || hovered) && (
          <motion.span
            layoutId={isActive ? 'nav-underline' : undefined}
            key={isActive ? 'active' : 'hover-' + href}
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            exit={{ scaleX: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full"
            style={{
              background: isActive ? 'white' : color,
              transformOrigin: 'left',
            }}
          />
        )}
      </AnimatePresence>
    </Link>
  )
}

/* ─── Mega Menu ─────────────────────────────────────────────────────────── */
/** Placeholder rows so an open menu never looks empty while /api/categories
 *  is still in flight (or after it failed) — that read as "broken nav". */
function CategorySkeleton({ rows, className }: { rows: number; className: string }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={className} aria-hidden="true">
          <span className="shrink-0 w-9 h-9 rounded-full bg-[#8B4513]/10 animate-pulse" />
          <span className="h-3 flex-1 rounded-full bg-[#8B4513]/10 animate-pulse" />
        </div>
      ))}
    </>
  )
}

function MegaMenu({ categories, loading }: { categories: Category[]; loading: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.18 }}
      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[600px] bg-[#FDF6EC] rounded-3xl shadow-[0_18px_50px_rgba(139,69,19,0.26)] border border-[#D2691E]/15 overflow-hidden z-50"
    >
      <div className="px-5 pt-5 pb-4">
        <p className="font-playfair text-[#2C1810] font-bold text-2xl leading-none">Onze producten</p>
        <p className="text-sm text-[#8B4513]/75 mt-1.5">Vers gebakken, elke dag</p>
      </div>

      <div className="px-4 pb-4 space-y-3">
        <MenuBanner
          href="/aanbiedingen"
          Icon={Tag}
          title="Aanbiedingen"
          subtitle="Bekijk onze actuele aanbiedingen"
          tone="sale"
        />

        <div className="grid grid-cols-2 gap-2.5">
          {loading && categories.length === 0 ? (
            <CategorySkeleton rows={6} className="flex items-center gap-3 p-2.5 rounded-2xl" />
          ) : (
            categories.map((cat) => <CategoryCard key={cat.id} cat={cat} />)
          )}
        </div>

        <MenuBanner
          href="/winkel"
          Icon={ShoppingBasket}
          title="Bekijk alle producten"
          subtitle="Ontdek ons complete assortiment"
          tone="shop"
        />
      </div>
    </motion.div>
  )
}

/* ─── Greeting helpers ───────────────────────────────────────────────────── */
function getHolidayGreeting(): string | null {
  const { month, day, year } = nlTimeParts()

  // Christmas (25–26 Dec)
  if (month === 12 && (day === 25 || day === 26)) return 'Prettige Kerstdagen!'
  // New Year's Eve / Day
  if ((month === 12 && day === 31) || (month === 1 && day === 1)) return 'Gelukkig Nieuwjaar!'
  // King's Day (27 Apr, or 26 Apr when 27 is Sunday)
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  if (month === 4 && (day === 27 || (day === 26 && dow === 6))) return 'Lekker Koningsdag!'
  // Easter — calculate for current year
  const easter = getEasterDate(year)
  if (month === easter.month && (day === easter.day || day === easter.day + 1)) return 'Vrolijk Pasen!'
  // Liberation Day (5 May)
  if (month === 5 && day === 5) return 'Fijne Bevrijdingsdag!'
  // Valentine's Day
  if (month === 2 && day === 14) return 'Fijne Valentijnsdag!'
  // Sinterklaas
  if (month === 12 && day === 5) return 'Vrolijk Sinterklaas!'
  return null
}

function getEasterDate(year: number): { month: number; day: number } {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day   = ((h + l - 7 * m + 114) % 31) + 1
  return { month, day }
}

function getTimeGreeting(): string {
  const { hour } = nlTimeParts()
  if (hour >= 5  && hour < 12) return 'Goedemorgen'
  if (hour >= 12 && hour < 18) return 'Goedemiddag'
  return 'Goedenavond'
}

/* ─── Account Dropdown ──────────────────────────────────────────────────── */
function AccountDropdown({ onClose }: { onClose: () => void }) {
  const { data: session } = useSession()
  const pathname        = usePathname()
  const holidayGreeting = getHolidayGreeting()
  const timeGreeting    = getTimeGreeting()
  const firstName       = session?.user?.name?.split(' ')[0] ?? null

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.18 }}
      className="absolute top-full right-0 mt-2 w-60 bg-[#FDF6EC] rounded-2xl shadow-[0_8px_30px_rgba(139,69,19,0.20)] border border-[#D2691E]/15 overflow-hidden z-50"
    >
      {session ? (
        <>
          <div className="px-4 py-3 border-b border-[#D2691E]/10">
            {holidayGreeting ? (
              <p className="font-semibold text-[#D2691E] text-sm">{holidayGreeting}</p>
            ) : (
              <p className="font-semibold text-[#2C1810] text-sm">
                {timeGreeting}{firstName ? `, ${firstName}` : ''}
              </p>
            )}
            <p className="text-xs text-[#8B4513] truncate mt-0.5">{session.user?.email}</p>
          </div>
          <div className="p-2">
            <Link
              href="/account"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#8B4513]/8 text-[#2C1810] text-sm transition-colors"
            >
              <User size={16} className="text-[#8B4513]" />
              Mijn account
            </Link>
            <Link
              href="/account?tab=bestellingen"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#8B4513]/8 text-[#2C1810] text-sm transition-colors"
            >
              <Package size={16} className="text-[#8B4513]" />
              Mijn bestellingen
            </Link>
            <Link
              href="/account?tab=favorieten"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#8B4513]/8 text-[#2C1810] text-sm transition-colors"
            >
              <Star size={16} className="text-[#8B4513]" />
              Favorieten
            </Link>
            {isStaff(session.user?.role) && (
              <Link
                href="/admin"
                onClick={onClose}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#8B4513]/8 text-[#2C1810] text-sm transition-colors"
              >
                <Settings size={16} className="text-[#8B4513]" />
                Beheer
              </Link>
            )}
            <button
              onClick={() => { signOut({ callbackUrl: '/' }); onClose() }}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-red-50 text-red-600 text-sm transition-colors mt-1"
            >
              <LogOut size={16} />
              Uitloggen
            </button>
          </div>
        </>
      ) : (
        <div className="p-2">
          <Link
            href={loginHref(pathname)}
            onClick={onClose}
            className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-[#8B4513]/8 text-[#2C1810] text-sm transition-colors font-medium"
          >
            <User size={16} className="text-[#8B4513]" />
            Inloggen
          </Link>
          <Link
            href="/registreren"
            onClick={onClose}
            className="flex items-center justify-center gap-2 mt-1 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors"
            style={{ background: '#8B4513', color: '#FDF6EC' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#D2691E')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#8B4513')}
          >
            Account aanmaken
          </Link>
        </div>
      )}
    </motion.div>
  )
}

/* ─── Mobile Menu ───────────────────────────────────────────────────────── */
function MobileMenu({
  isOpen,
  onClose,
  categories,
  catsLoading,
  pathname,
  navLinks,
}: {
  isOpen:      boolean
  onClose:     () => void
  categories:  Category[]
  catsLoading: boolean
  pathname:    string
  navLinks:    NavLink[]
}) {
  const { data: session } = useSession()
  const [winkelOpen,  setWinkelOpen]  = useState(false)
  const [genietOpen,  setGenietOpen]  = useState(false)
  /* No hover on touch — the Winkel icon plays when the section is tapped open,
     and idles on a loop for as long as its row is scrolled into view. */
  const shopIconRef = useRef<AnimatedIconHandle>(null)
  const shopBtnRef  = useRef<HTMLButtonElement>(null)
  useAnimatedIconLoop(shopBtnRef, shopIconRef, { delay: 3 * ICON_LOOP_STAGGER })

  useEffect(() => {
    if (!isOpen) { setWinkelOpen(false); setGenietOpen(false) }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.nav
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 z-[100] w-[85vw] max-w-sm bg-[#FDF6EC] flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#D2691E]/20">
              <div>
                <p className="font-playfair font-bold text-[#8B4513] text-xl leading-none">
                  Bakkerij Voortman
                </p>
                <p className="font-dancing text-[#D2691E] text-sm mt-0.5">
                  Ovenvers en overheerlijk
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-[#8B4513] hover:bg-[#8B4513]/10 transition-colors"
                aria-label="Menu sluiten"
              >
                <X size={22} />
              </button>
            </div>

            {/* Nav Links */}
            <div className="flex-1 overflow-y-auto py-4 px-3">
              {navLinks.map((link) => (
                <div key={link.href}>
                  {link.hasSubLinks ? (
                    <>
                      <button
                        onClick={() => setGenietOpen((p) => !p)}
                        className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-[#2C1810] font-semibold hover:bg-[#8B4513]/8 transition-colors"
                      >
                        <span>{link.label}</span>
                        <motion.span animate={{ rotate: genietOpen ? 180 : 0 }}>
                          <ChevronDown size={18} className="text-[#8B4513]" />
                        </motion.span>
                      </button>
                      <AnimatePresence>
                        {genietOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden pl-4"
                          >
                            {GENIETEN_SUB_LINKS.map((sub, i) => (
                              <GenietSubLink
                                key={sub.href}
                                sub={sub}
                                index={i}
                                onClick={onClose}
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors ${
                                  pathname === sub.href ? 'bg-[#8B4513]/12 text-[#8B4513]' : 'text-[#2C1810] hover:bg-[#8B4513]/8'
                                }`}
                                labelClassName="text-sm font-medium"
                              />
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : link.hasMenu ? (
                    <>
                      <button
                        ref={shopBtnRef}
                        onClick={() => {
                          setWinkelOpen((p) => !p)
                          shopIconRef.current?.startAnimation()
                        }}
                        className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-[#2C1810] font-semibold hover:bg-[#8B4513]/8 transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <StoreIcon ref={shopIconRef} size={18} className="text-[#8B4513]" />
                          {link.label}
                        </span>
                        <motion.span animate={{ rotate: winkelOpen ? 180 : 0 }}>
                          <ChevronDown size={18} className="text-[#8B4513]" />
                        </motion.span>
                      </button>
                      <AnimatePresence>
                        {winkelOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden pl-4"
                          >
                            <div className="space-y-2.5 pb-1">
                              <MenuBanner
                                href="/aanbiedingen"
                                onClick={onClose}
                                Icon={Tag}
                                title="Aanbiedingen"
                                subtitle="Bekijk onze actuele aanbiedingen"
                                tone="sale"
                              />

                              {catsLoading && categories.length === 0 ? (
                                <CategorySkeleton rows={4} className="flex items-center gap-3 p-2.5 rounded-2xl" />
                              ) : (
                                categories.map((cat) => (
                                  <CategoryCard key={cat.id} cat={cat} onClick={onClose} />
                                ))
                              )}

                              {/* De hele winkel sluit de rij: op een telefoon
                                  scrol je langs elke categorie voordat je hier
                                  bent, dus hoort hij onderaan. */}
                              <MenuBanner
                                href="/winkel"
                                onClick={onClose}
                                Icon={ShoppingBasket}
                                title="Bekijk alle producten"
                                subtitle="Ontdek ons complete assortiment"
                                tone="shop"
                              />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : link.isPasen ? (
                    <Link
                      href={link.href}
                      onClick={onClose}
                      className="flex items-center gap-2 px-4 py-3 rounded-xl font-bold transition-all"
                      style={{
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.12), rgba(236,72,153,0.12))',
                        color: '#A855F7',
                        border: '1.5px solid rgba(168,85,247,0.3)',
                      }}
                    >
                      <Egg size={16} />
                      {link.label}
                    </Link>
                  ) : link.isCampaign ? (
                    <Link
                      href={link.href}
                      onClick={onClose}
                      className="flex items-center gap-2 px-4 py-3 rounded-xl font-bold transition-all"
                      style={{
                        background: `${link.accentColor ?? '#D2691E'}1A`,
                        color:      link.accentColor ?? '#D2691E',
                        border:     `1.5px solid ${link.accentColor ?? '#D2691E'}4D`,
                      }}
                    >
                      <Sparkles size={16} />
                      {link.label}
                    </Link>
                  ) : (
                    <Link
                      href={link.href}
                      onClick={onClose}
                      className={`block px-4 py-3 rounded-xl font-semibold transition-colors ${
                        pathname === link.href
                          ? 'bg-[#8B4513]/12 text-[#8B4513]'
                          : 'text-[#2C1810] hover:bg-[#8B4513]/8'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-[#D2691E]/20 p-4 space-y-2">
              {session ? (
                <>
                  <Link
                    href="/account"
                    onClick={onClose}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl text-[#2C1810] hover:bg-[#8B4513]/8 font-semibold transition-colors text-sm"
                  >
                    <User size={16} className="text-[#8B4513]" />
                    Mijn account
                  </Link>
                  {isStaff(session.user?.role) && (
                    <Link
                      href="/admin"
                      onClick={onClose}
                      className="flex items-center gap-2 px-4 py-3 rounded-xl text-[#2C1810] hover:bg-[#8B4513]/8 font-semibold transition-colors text-sm"
                    >
                      <Settings size={16} className="text-[#8B4513]" />
                      Beheer
                    </Link>
                  )}
                  <button
                    onClick={() => { signOut({ callbackUrl: '/' }); onClose() }}
                    className="flex items-center gap-2 w-full px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 font-semibold transition-colors text-sm"
                  >
                    <LogOut size={16} />
                    Uitloggen
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    href={loginHref(pathname)}
                    onClick={onClose}
                    className="text-center px-4 py-3 rounded-xl border-2 border-[#8B4513] text-[#8B4513] font-semibold hover:bg-[#8B4513]/8 transition-colors text-sm"
                  >
                    Inloggen
                  </Link>
                  <Link
                    href="/registreren"
                    onClick={onClose}
                    className="text-center px-4 py-3 rounded-xl font-semibold transition-colors text-sm"
                    style={{ background: '#8B4513', color: '#FDF6EC' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#D2691E')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#8B4513')}
                  >
                    Registreren
                  </Link>
                </div>
              )}
            </div>
          </motion.nav>
        </>
      )}
    </AnimatePresence>
  )
}

/* ─── Single notification card ──────────────────────────────────────────── */
function NotifCard({
  notif,
  onDismiss,
  onViewCart,
  onUndo,
}: {
  notif: CartNotification
  onDismiss: () => void
  onViewCart: () => void
  onUndo: () => void
}) {
  const unitPrice = itemEffectivePrice(notif)
  const variantSummary = notif.customizations?.map((c) => c.optionName).join(', ')
  useEffect(() => {
    const id = setTimeout(onDismiss, 5000)
    return () => clearTimeout(id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0,   scale: 1    }}
      exit={{    opacity: 0, y: -10, scale: 0.95 }}
      transition={{ type: 'spring', damping: 22, stiffness: 320 }}
      className="rounded-2xl overflow-hidden"
      style={{
        background: '#FDF6EC',
        border: '1.5px solid rgba(139,69,19,0.18)',
        boxShadow: '0 12px 40px rgba(44,24,16,0.22)',
      }}
    >
      {/* Accent bar */}
      <div className="h-1" style={{ background: 'linear-gradient(90deg, #8B4513, #D2691E, #DAA520)' }} />

      <div style={{ padding: '12px 14px' }}>
        {/* Top row: image + details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Product image */}
          <div style={{ width: 48, height: 48, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: 'rgba(210,105,30,0.10)' }}>
            {notif.image ? (
              <img
                src={notif.image}
                alt={notif.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Wheat size={20} style={{ color: 'rgba(210,105,30,0.5)' }} /></div>
            )}
          </div>

          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#22C55E', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 12 12" fill="none" style={{ width: 9, height: 9 }}>
                  <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#16A34A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {notif.addedQty > 1 ? `Toegevoegd · ${notif.addedQty}×` : 'Toegevoegd'}
              </span>
            </div>
            <p style={{ fontWeight: 600, color: '#2C1810', fontSize: 13, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{notif.name}</p>
            {variantSummary && (
              <p style={{ fontSize: 10.5, color: '#75665D', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{variantSummary}</p>
            )}
            <p style={{ fontSize: 11, fontWeight: 700, color: '#8B4513', marginTop: 2 }}>
              €{unitPrice.toFixed(2).replace('.', ',')}
              {notif.addedQty > 1 && (
                <span style={{ fontWeight: 500, color: '#75665D' }}> · €{(unitPrice * notif.addedQty).toFixed(2).replace('.', ',')} totaal</span>
              )}
            </p>
          </div>
        </div>

        {/* Actions — full-width row, no overlap */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button
            onClick={onViewCart}
            style={{ flex: 1, background: '#8B4513', color: '#FDF6EC', border: 'none', borderRadius: 9, padding: '8px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Bekijk
          </button>
          <button
            onClick={onUndo}
            style={{ flexShrink: 0, background: 'transparent', border: '1px solid rgba(139,69,19,0.25)', borderRadius: 9, padding: '8px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#8B4513', whiteSpace: 'nowrap' }}
          >
            Ongedaan maken
          </button>
        </div>
      </div>

      {/* Progress bar auto-dismiss */}
      <motion.div
        initial={{ width: '100%' }}
        animate={{ width: '0%' }}
        transition={{ duration: 5, ease: 'linear' }}
        style={{ height: 2, background: '#D2691E' }}
      />
    </motion.div>
  )
}

/* ─── Cart Button + Added Popup ─────────────────────────────────────────── */
function CartButton({ iconColorClass, itemCount }: { iconColorClass: string; itemCount: number }) {
  const { openCart, notifications, removeNotification, decrementItem } = useCartStore()

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={openCart}
        className={`relative p-2.5 rounded-lg transition-colors ${iconColorClass}`}
        aria-label={`Winkelwagen, ${itemCount} items`}
      >
        <ShoppingCart size={20} />
        {itemCount > 0 && (
          <motion.span
            key={itemCount}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold leading-none"
            style={{ background: '#D2691E', color: '#FDF6EC' }}
          >
            {itemCount > 99 ? '99+' : itemCount}
          </motion.span>
        )}
      </button>

      <div
        style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: 280,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 100,
        }}
      >
        <AnimatePresence mode="popLayout">
          {notifications.map((notif) => (
            <NotifCard
              key={notif.notifId}
              notif={notif}
              onDismiss={() => removeNotification(notif.notifId)}
              onViewCart={() => { removeNotification(notif.notifId); openCart() }}
              onUndo={() => { decrementItem(notif.cartKey, notif.addedQty); removeNotification(notif.notifId) }}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
}

/* ─── Navbar (main export) ──────────────────────────────────────────────── */
/** Pagina's zonder donkere kop, waar de navigatiebalk meteen donker moet zijn. */
const AUTH_PAGES = new Set(['/inloggen', '/registreren', '/wachtwoord-vergeten', '/wachtwoord-herstellen'])

export default function Navbar() {
  const pathname                        = usePathname()
  const { totalItems, openCart }        = useCartStore()
  const [mounted, setMounted]           = useState(false)
  const [scrolled, setScrolled]         = useState(false)
  const [mobileOpen, setMobileOpen]     = useState(false)
  const [searchOpen, setSearchOpen]     = useState(false)
  const [shopMenuOpen,     setShopMenuOpen]     = useState(false)
  const [genietMenuOpen,   setGenietMenuOpen]   = useState(false)
  const [accountOpen,      setAccountOpen]      = useState(false)
  const [categories,       setCategories]       = useState<Category[]>([])
  const [catsLoading,      setCatsLoading]      = useState(true)
  const [activeCampaigns,  setActiveCampaigns]  = useState<{ slug: string; name: string; accentColor: string }[]>([])
  const shopMenuRef  = useRef<HTMLDivElement>(null)
  const shopIconRef  = useRef<AnimatedIconHandle>(null)
  const genietMenuRef = useRef<HTMLDivElement>(null)
  /* The Winkel pill sits in the bar itself, so it idles for as long as the
     header is on screen. On a phone the desktop nav is display:none and never
     intersects, so this costs nothing there. */
  useAnimatedIconLoop(shopMenuRef, shopIconRef, { delay: 3 * ICON_LOOP_STAGGER })
  const accountRef   = useRef<HTMLDivElement>(null)

  useEffect(() => { setMounted(true) }, [])

  /* Scroll detection */
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12)
    window.addEventListener('scroll', handler, { passive: true })
    handler()
    return () => window.removeEventListener('scroll', handler)
  }, [])

  /* Close mobile menu on route change */
  useEffect(() => { setMobileOpen(false) }, [pathname])

  /* Fetch categories. The nav shows TOP-LEVEL categories only — an
     alphabetical slice of the raw list mixed parents with their own children
     ("Brood" next to "Gevuld brood") and ran out at the letter G, so Taart &
     vlaai never made it into the menu.

     Een mislukte of lege respons liet het menu leeg achter tot een harde
     refresh: er werd drie keer geprobeerd en daarna nooit meer. Nu wordt er
     vaker geprobeerd, wordt een lege lijst als mislukking geteld, gaat het
     verzoek langs de browsercache heen en krijgt het menu bij openen nog een
     kans. */
  const catsAttempt = useRef(0)
  const catsTimer   = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const catsRetried = useRef(false)

  const loadCategories = useCallback(() => {
    function attempt() {
      clearTimeout(catsTimer.current)
      setCatsLoading(true)
      fetch('/api/categories', { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((data: Category[]) => {
          const list = Array.isArray(data) ? data : []
          if (list.length === 0) throw new Error('lege categorielijst')
          const roots = list.filter((c) => !c.parentId)
          setCategories((roots.length ? roots : list).slice(0, 8))
          catsAttempt.current = 0
          setCatsLoading(false)
        })
        .catch(() => {
          catsAttempt.current += 1
          if (catsAttempt.current > 4) { setCatsLoading(false); return }
          catsTimer.current = setTimeout(attempt, catsAttempt.current * 600)
        })
    }
    attempt()
  }, [])

  useEffect(() => {
    loadCategories()
    return () => clearTimeout(catsTimer.current)
  }, [loadCategories])

  /* Het menu opengaan is een tweede kans: is de lijst leeg gebleven, dan
     halen we hem alsnog op in plaats van een leeg menu te tonen. */
  useEffect(() => {
    const open = shopMenuOpen || mobileOpen
    if (!open) { catsRetried.current = false; return }
    if (catsRetried.current || catsLoading || categories.length > 0) return
    catsRetried.current = true
    catsAttempt.current = 0
    loadCategories()
  }, [shopMenuOpen, mobileOpen, catsLoading, categories.length, loadCategories])

  /* Fetch active campaigns (drives which seasonal nav links show up).
     Refreshed on every route change so an in/uitgeschakelde campagne meteen
     in de navigatie verschijnt/verdwijnt — zonder harde refresh. */
  useEffect(() => {
    fetch('/api/campaigns/active', { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setActiveCampaigns(Array.isArray(data) ? data : []))
      .catch(() => {})
  }, [pathname])

  const navLinks: NavLink[] = [
    ...BASE_NAV_LINKS.slice(0, 4), // Home, Ons Verhaal, Genieten, Winkel
    ...activeCampaigns.map((c) => c.slug === 'pasen'
      ? { label: c.name, href: '/pasen',                isCampaign: true, isPasen: true, accentColor: c.accentColor }
      : c.slug === 'sinterklaas'
      // Het jaartal staat al in de naam van het seizoen; in het menu is het te lang.
      ? { label: c.name.replace(/\s*\d{4}$/, ''), href: '/sinterklaas', isCampaign: true, accentColor: c.accentColor }
      : { label: c.name, href: `/campagne/${c.slug}`,   isCampaign: true,                accentColor: c.accentColor }
    ),
    ...BASE_NAV_LINKS.slice(4), // Openingstijden, Contact
  ]

  /* Outside click handlers */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (shopMenuRef.current && !shopMenuRef.current.contains(e.target as Node))
        setShopMenuOpen(false)
      if (genietMenuRef.current && !genietMenuRef.current.contains(e.target as Node))
        setGenietMenuOpen(false)
      if (accountRef.current && !accountRef.current.contains(e.target as Node))
        setAccountOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleCloseSearch = useCallback(() => setSearchOpen(false), [])

  const itemCount = mounted ? totalItems() : 0

  /* ── De back-office heeft zijn eigen schil ──────────────────────────────
     Daar staat de zijbalk van /admin over de volle hoogte van het scherm en
     zou deze balk er alleen maar overheen liggen. De winkel blijft bereikbaar
     via "Website bekijken" onderin dat menu. De voettekst verdwijnt daar om
     dezelfde reden (zie Footer.tsx). */
  if (pathname.startsWith('/admin')) return null

  /* ── Force dark navbar on /winkel sub-pages and on the pages with a crème
        background and no dark hero (inloggen, registreren, wachtwoord-*):
        there the transparent navbar put white text on crème. ── */
  const forceDark =
    pathname.startsWith('/winkel/') ||
    pathname === '/bestelling-bevestigd' ||
    AUTH_PAGES.has(pathname) ||
    pathname.startsWith('/account/')

  /* ── Dark when scrolled OR on winkel pages ── */
  const isDark = scrolled || forceDark

  /* ── Text-shadow style for transparent state ── */
  const textShadow = '0 1px 3px rgba(0,0,0,0.8)'

  /* ── Link color classes based on dark state ── */
  const linkColorClass = isDark
    ? 'text-white hover:text-white/80'
    : 'hover:text-white/80'

  const activeLinkClass = 'text-white'

  const iconColorClass = 'text-white hover:text-white/80 hover:bg-white/15'

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[80] transition-all duration-300 ${
          isDark
            // Op de Sinterklaasfilm is de balk de bovenste filmbalk: zwart.
            ? pathname === '/sinterklaas'
              ? 'bg-black shadow-[0_2px_20px_rgba(0,0,0,0.4)]'
              : 'bg-[#2C1810] backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.4)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">

            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 group" aria-label="Bakkerij Voortman Rijssen, naar de homepage">
              <img
                src="/images/logo-solo.png"
                alt=""
                className="transition-all duration-300 shrink-0 group-hover:scale-110 group-hover:brightness-125"
                style={{ width: 44, height: 44, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
              />
              {/* Tussen lg en 1400px alleen het beeldmerk: met een seizoen erbij past het menu anders niet. */}
              <div className="leading-none lg:hidden min-[1400px]:block">
                <span
                  className="font-playfair font-bold block whitespace-nowrap transition-colors text-white group-hover:text-white/85"
                  style={{ fontSize: '1.55rem', textShadow: isDark ? 'none' : textShadow }}
                >
                  Bakkerij Voortman
                </span>
                <span
                  className="font-dancing block -mt-0.5"
                  style={{
                    fontSize: '1.1rem',
                    color: '#F4A460',
                    textShadow: isDark ? 'none' : textShadow,
                  }}
                >
                  Ovenvers en overheerlijk
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="Hoofdnavigatie">
              {navLinks.map((link) =>
                link.hasSubLinks ? (
                  <div key={link.href} ref={genietMenuRef} className="relative"
                    onMouseEnter={() => { setGenietMenuOpen(true); setShopMenuOpen(false) }}
                    onMouseLeave={() => setGenietMenuOpen(false)}
                  >
                    <button
                      onClick={() => setGenietMenuOpen((p) => !p)}
                      className="flex items-center gap-1.5 relative px-3 xl:px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap"
                      style={{ color: (pathname.startsWith('/genieten-vieren')) ? 'white' : '#F4A460', textShadow: isDark ? undefined : textShadow }}
                      aria-expanded={genietMenuOpen}
                    >
                      {link.label}
                      <motion.span animate={{ rotate: genietMenuOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDown size={13} />
                      </motion.span>
                    </button>
                    <AnimatePresence>
                      {genietMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.18 }}
                          className="absolute top-full left-0 mt-2 w-max min-w-[13rem] bg-[#FDF6EC] rounded-2xl shadow-[0_8px_30px_rgba(139,69,19,0.2)] border border-[#D2691E]/15 overflow-hidden z-50 p-2"
                        >
                          {GENIETEN_SUB_LINKS.map((sub, i) => (
                            <GenietSubLink
                              key={sub.href}
                              sub={sub}
                              index={i}
                              onClick={() => setGenietMenuOpen(false)}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#8B4513]/8 transition-colors ${
                                pathname === sub.href ? 'bg-[#8B4513]/8 text-[#8B4513]' : 'text-[#2C1810]'
                              }`}
                              labelClassName="text-sm font-medium whitespace-nowrap"
                            />
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : link.hasMenu ? (
                  <div key={link.href} ref={shopMenuRef} className="relative"
                    onMouseEnter={() => {
                      setShopMenuOpen(true); setGenietMenuOpen(false)
                      shopIconRef.current?.startAnimation()
                    }}
                    onMouseLeave={() => { setShopMenuOpen(false); shopIconRef.current?.stopAnimation() }}
                  >
                    <motion.button
                      onClick={() => setShopMenuOpen((p) => !p)}
                      whileHover={{ boxShadow: '0 0 16px rgba(244,164,96,0.45)' }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center gap-1.5 px-4 py-1.5 rounded-full font-bold text-sm"
                      style={{
                        background: 'rgba(244,164,96,0.22)',
                        color: '#F4A460',
                        border: '1.5px solid rgba(244,164,96,0.5)',
                      }}
                      aria-expanded={shopMenuOpen}
                      aria-haspopup="true"
                    >
                      <StoreIcon ref={shopIconRef} size={18} />
                      {link.label}
                      <motion.span animate={{ rotate: shopMenuOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDown size={15} />
                      </motion.span>
                    </motion.button>
                    <AnimatePresence>
                      {shopMenuOpen && <MegaMenu categories={categories} loading={catsLoading} />}
                    </AnimatePresence>
                  </div>
                ) : link.isPasen ? (
                  <motion.div
                    key={link.href}
                    className="ml-2"
                    animate={{ y: [0, -10, 0, -5, 0, -2, 0] }}
                    transition={{
                      duration: 0.75,
                      times: [0, 0.18, 0.42, 0.58, 0.74, 0.87, 1],
                      ease: 'easeInOut',
                      repeat: Infinity,
                      repeatDelay: 15,
                    }}
                  >
                    <Link
                      href={link.href}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all group"
                      style={{
                        background: pathname === '/pasen' ? 'rgba(168,85,247,0.18)' : 'transparent',
                        color: pathname === '/pasen' ? '#D8B4FE' : 'rgba(255,255,255,0.75)',
                        border: pathname === '/pasen' ? '1px solid rgba(168,85,247,0.35)' : '1px solid transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(168,85,247,0.14)'
                        e.currentTarget.style.color = '#D8B4FE'
                        e.currentTarget.style.border = '1px solid rgba(168,85,247,0.30)'
                      }}
                      onMouseLeave={(e) => {
                        if (pathname !== '/pasen') {
                          e.currentTarget.style.background = 'transparent'
                          e.currentTarget.style.color = 'rgba(255,255,255,0.75)'
                          e.currentTarget.style.border = '1px solid transparent'
                        }
                      }}
                    >
                      <Egg size={13} style={{ color: '#C084FC', flexShrink: 0 }} />
                      {link.label}
                    </Link>
                  </motion.div>
                ) : link.isCampaign ? (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="flex items-center gap-1.5 ml-1 xl:ml-2 px-2.5 xl:px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all"
                    style={(() => {
                      const accent = link.accentColor ?? '#D2691E'
                      const active = pathname === link.href
                      return {
                        background: active ? `${accent}33` : 'transparent',
                        color:      active ? '#FDF6EC' : 'rgba(255,255,255,0.75)',
                        border:     active ? `1px solid ${accent}66` : '1px solid transparent',
                      }
                    })()}
                  >
                    <Sparkles size={13} style={{ color: link.accentColor ?? '#D2691E', flexShrink: 0 }} />
                    {link.label}
                  </Link>
                ) : (
                  <NavLink
                    key={link.href}
                    href={link.href}
                    isActive={pathname === link.href}
                    color="#F4A460"
                    textShadow={isDark ? undefined : textShadow}
                  >
                    {link.label}
                  </NavLink>
                )
              )}
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-1">

              {/* Search */}
              <button
                onClick={() => setSearchOpen(true)}
                className={`p-2.5 rounded-lg transition-colors ${iconColorClass}`}
                aria-label="Zoeken"
              >
                <Search size={20} />
              </button>

              {/* Account (desktop only) */}
              <div ref={accountRef} className="relative hidden lg:block">
                <button
                  onClick={() => setAccountOpen((p) => !p)}
                  className={`p-2.5 rounded-lg transition-colors ${iconColorClass}`}
                  aria-label="Account"
                  aria-expanded={accountOpen}
                >
                  <User size={20} />
                </button>
                <AnimatePresence>
                  {accountOpen && <AccountDropdown onClose={() => setAccountOpen(false)} />}
                </AnimatePresence>
              </div>

              {/* Cart */}
              <CartButton iconColorClass={iconColorClass} itemCount={itemCount} />

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(true)}
                className={`lg:hidden p-2.5 rounded-lg transition-colors ml-1 ${iconColorClass}`}
                aria-label="Menu openen"
              >
                <Menu size={22} />
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <MobileMenu
        isOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        categories={categories}
        catsLoading={catsLoading}
        pathname={pathname}
        navLinks={navLinks}
      />

      {/* Search overlay */}
      <AnimatePresence>
        {searchOpen && <SearchOverlay onClose={handleCloseSearch} />}
      </AnimatePresence>
    </>
  )
}
